import { ConnectionType } from 'primary-schema';

import { type Common } from './common.js';
import {
  type Connection,
  type ConnectionApi,
  type ConnectionIntegrationEngine,
  type ConnectionInterfaceEngine,
  type ConnectionResource,
  type ConnectionSftp,
  type CreateInterfaceEngineConnectorValues,
  type CreateSftpConnectionResponse,
  type CreateSftpConnectionValues,
  type FindAllOptions,
  type FindInterfaceEngineEventsOptions,
  type GetConnectionResourcesOptions,
  type GetFileSignedUrlOptions,
  type InterfaceEngineChannel,
  type InterfaceEngineEventsResponse,
  type ResourceGatewaySftpConnectionRawResponse,
  SftpSourceGeneration,
  type SignedUrlResponse,
  type UpdateSftpConnectionResponse,
  type UpdateSftpConnectionValues,
} from './models/connections/index.js';

export class API {
  common: Common;
  baseEndpoint = '/connections';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions): Promise<{ connections: Connection[] }> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(args: {
    /**
     * FIXME: Remove detailPath as a parameter once we have one endpoint that handles all connection types.
     */
    detailPath: string;
  }): Promise<
    ConnectionApi | ConnectionSftp | ConnectionInterfaceEngine | ConnectionIntegrationEngine
  > {
    const { detailPath } = args;
    const url = this.common.constructPath(detailPath);
    const response = await this.common.getJSON(url);
    if ('connectionChannel' in response) {
      return {
        ...response,
        connectionType: ConnectionType.Sftp,
        sourceGeneration: SftpSourceGeneration.Channel,
      };
    }
    return response.connection;
  }

  async findEvents(
    options: FindInterfaceEngineEventsOptions
  ): Promise<InterfaceEngineEventsResponse> {
    const { detailPath, ...rest } = options;
    const url = this.common.constructPath(detailPath, rest);
    return await this.common.getJSON(url);
  }

  async createInterfaceEngineConnector(
    values: CreateInterfaceEngineConnectorValues
  ): Promise<InterfaceEngineChannel> {
    const url = this.common.constructPath(`${this.baseEndpoint}/interface-engine/sftp`);
    return await this.common.postJSON(url, values);
  }

  async createSftpConnection(
    values: CreateSftpConnectionValues
  ): Promise<CreateSftpConnectionResponse> {
    const url = this.common.constructPath('/resource-gateway/connections/sftp');
    const response = (await this.common.postJSON(
      url,
      values
    )) as ResourceGatewaySftpConnectionRawResponse;
    return {
      ...response,
      connectionType: ConnectionType.Sftp,
      sourceGeneration: SftpSourceGeneration.Channel,
    };
  }

  async updateSftpConnection(
    id: string,
    values: UpdateSftpConnectionValues
  ): Promise<UpdateSftpConnectionResponse> {
    const url = this.common.constructPath(`/resource-gateway/connections/sftp/${id}`);
    const response = (await this.common.patchJSON(
      url,
      values
    )) as ResourceGatewaySftpConnectionRawResponse;
    return {
      ...response,
      connectionType: ConnectionType.Sftp,
      sourceGeneration: SftpSourceGeneration.Channel,
    };
  }

  /**
   * Get resource types for a connection (via resource-gateway proxy)
   */
  async getConnectionResourceTypes(args: {
    connectionId: string;
  }): Promise<{ resourceTypes: { resourceKey: string; name: string }[] }> {
    const { connectionId } = args;
    const url = this.common.constructPath(
      `/resource-gateway/connections/${connectionId}/resource-types`
    );
    return await this.common.getJSON(url);
  }

  /**
   * Get resources for a connection (via resource-gateway proxy)
   */
  async getConnectionResources(options: GetConnectionResourcesOptions): Promise<{
    resources: ConnectionResource[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const { connectionId, resourceKey, limit = 25, offset = 0 } = options;
    const url = this.common.constructPath(
      `/resource-gateway/connections/${connectionId}/resources`,
      { resourceKey, limit, offset }
    );
    return await this.common.getJSON(url);
  }

  /**
   * Get a signed URL for previewing/downloading a file (via resource-gateway proxy)
   */
  async getFileSignedUrl(options: GetFileSignedUrlOptions): Promise<SignedUrlResponse> {
    const { fileId, organizationId, disposition = 'inline' } = options;
    const url = this.common.constructPath(
      `/resource-gateway/resource-access/files/${fileId}/signed-url`,
      { organizationId, disposition }
    );
    return await this.common.getJSON(url);
  }
}
